import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';
import LogoImage from '../common/LogoImage';

const ForcePasswordChange = () => {
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const { user, setUser, setIsAuthenticated, updateToken, loadUser, setNeedsPasswordChange } = useContext(AuthContext);
  const navigate = useNavigate();
  
  const { newPassword, confirmPassword } = formData;
  
  const onChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setError('');
    
    // Validate passwords
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }
    
    // Check if password is the default one
    if (newPassword === 'Welcome123') {
      setError('You cannot use the default password. Please choose a different password.');
      return;
    }
    
    try {
      setLoading(true);
      
      // Call API to change password
      console.log('Changing password for user:', user ? user.id : 'undefined');
      
      // Make sure we have a token
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Call the API with the correct endpoint
      await axios.post('/api/change-password', { 
        newPassword,
        userId: user ? user.id : undefined
      }, {
        headers: {
          'x-auth-token': token
        }
      });
      
      setSuccess(true);
      console.log('Password changed successfully, now refreshing authentication token');
      
      // Update authentication state directly without re-authenticating
      try {
        console.log('Updating user state after password change');
        
        // Explicitly set needsPasswordChange to false in AuthContext
        setNeedsPasswordChange(false);
        
        // Update user in context to reflect password change
        if (user) {
          const updatedUser = {
            ...user,
            isFirstLogin: false
          };
          setUser(updatedUser);
        }
        
        console.log('User state updated after password change');
        
        // Force reload user data from server with current token
        await loadUser(true);
        
      } catch (updateErr) {
        console.error('Error updating user state after password change:', updateErr);
        // Continue with the flow even if state update fails
      }
      
      // Set flag to show tutorial after redirect
      localStorage.setItem('showTutorial', 'true');
      
      // Redirect to dashboard immediately
      console.log('Redirecting to dashboard');
      navigate('/');
      
    } catch (err) {
      console.error('Password change error:', err.response?.data || err.message);
      setError(err.response?.data?.msg || 'Failed to change password. Please try again.');
      setLoading(false);
    }
  };
  
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      backgroundColor: '#FFFAFA',
      padding: '20px'
    },
    logo: {
      marginBottom: '30px',
      maxWidth: '400px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '100%'
    },
    card: {
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
      padding: '30px',
      width: '100%',
      maxWidth: '450px'
    },
    title: {
      color: '#243444',
      textAlign: 'center',
      marginBottom: '20px',
      fontWeight: 'bold'
    },
    alert: {
      backgroundColor: '#f8d7da',
      color: '#721c24',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    success: {
      backgroundColor: '#d4edda',
      color: '#155724',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px'
    },
    formGroup: {
      marginBottom: '20px'
    },
    label: {
      display: 'block',
      marginBottom: '5px',
      color: '#4C5454',
      fontWeight: 'bold'
    },
    input: {
      width: '100%',
      padding: '10px',
      borderRadius: '4px',
      border: '1px solid #ddd',
      fontSize: '16px'
    },
    button: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#243444',
      color: '#D2BA7B',
      border: 'none',
      borderRadius: '4px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      transition: 'background-color 0.3s'
    },
    info: {
      backgroundColor: '#e2f3f5',
      color: '#0c5460',
      padding: '10px',
      borderRadius: '4px',
      marginBottom: '20px',
      fontSize: '14px'
    }
  };
  
  return (
    <div style={styles.container}>
      <div style={styles.logo}>
        <LogoImage 
          type="transparent" 
          style={{ maxWidth: '100%', height: '150px' }}
        />
        <h2 style={{ color: '#243444', textAlign: 'center', marginTop: '10px' }}>Business Resilience Navigator</h2>
      </div>
      <div style={styles.card}>
        <h1 style={styles.title}>Change Your Password</h1>
        
        <div style={styles.info}>
          <p>For security reasons, you need to change your initial password before continuing.</p>
        </div>
        
        {error && <div style={styles.alert}>{error}</div>}
        {success && <div style={styles.success}>Password changed successfully! Redirecting to dashboard...</div>}
        
        {!success && (
          <form onSubmit={onSubmit}>
            <div style={styles.formGroup}>
              <label htmlFor="newPassword" style={styles.label}>New Password</label>
              <input
                type="password"
                name="newPassword"
                id="newPassword"
                value={newPassword}
                onChange={onChange}
                required
                style={styles.input}
                minLength="8"
              />
            </div>
            <div style={styles.formGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>Confirm Password</label>
              <input
                type="password"
                name="confirmPassword"
                id="confirmPassword"
                value={confirmPassword}
                onChange={onChange}
                required
                style={styles.input}
                minLength="8"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              style={styles.button}
            >
              {loading ? 'Changing Password...' : 'Change Password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForcePasswordChange;
